import type { LandingContent } from "./types";
import heroLoftAsset from "@/assets/loft-insulation/hero-loft.jpg.asset.json";
import loftStep1Asset from "@/assets/loft-insulation/loft-step-1.jpg.asset.json";
import loftStep2Asset from "@/assets/loft-insulation/loft-step-2.jpg.asset.json";
import loftStep3Asset from "@/assets/loft-insulation/loft-step-3.jpg.asset.json";
import loftStep4Asset from "@/assets/loft-insulation/loft-step-4.jpg.asset.json";
import loftEvidenceAsset from "@/assets/loft-insulation/loft-evidence.jpg.asset.json";
import loftCompletionAsset from "@/assets/loft-insulation/loft-completion-pack.jpg.asset.json";
import loftSignColdAsset from "@/assets/loft-insulation/signs/cold-upstairs-rooms.png.asset.json";

const heroLoft = heroLoftAsset.url;

/**
 * Body content for the /loft-insulation page. Drives the shared
 * LpOrderedSections renderer (process, trust, testimonials, pricing, FAQ).
 *
 * Loft-specific bands (inspect-first, signs, on-the-day, completion,
 * not-every-loft, why-correct, ventilation, social-housing) are inlined
 * in `src/components/landing/lp-loft-insulation.tsx`.
 *
 * Repositioning rules: no "independent" / "no commission" language; no
 * £ savings figures; no guarantee durations.
 */
export const loftInsulationLp: LandingContent = {
  slug: "loft-insulation",
  path: "/lp/loft-insulation",
  title: "Loft Insulation In South Wales",
  metaTitle: "Loft Insulation South Wales | PAS 2030 Certified | Free Inspection",
  metaDescription:
    "PAS 2030 certified loft insulation across South Wales. Free loft inspection, insurance-backed guarantee, clear property-specific quotation. Call 0800 009 2992.",
  defaultService: "Loft Insulation",
  heroHeadline: "Loft Insulation In South Wales",
  heroSubheading:
    "Inspected properly. Installed cleanly. Quoted clearly. Across Wales and the South of England.",
  heroBullets: [
    "PAS 2030 Certified",
    "Free Loft Inspection",
    "Insurance-Backed Guarantee",
    "No Obligation",
    "Ventilation Checked Before Install",
  ],
  heroImage: heroLoft,
  heroAlt: "Well-insulated UK loft with mineral wool laid between joists and clear eaves ventilation",
  heroFloatImages: [
    { src: heroLoft, slug: "loft-hero" },
    { src: loftStep1Asset.url, slug: "loft-step1" },
    { src: loftStep2Asset.url, slug: "loft-step2" },
    { src: loftStep3Asset.url, slug: "loft-step3" },
    { src: loftStep4Asset.url, slug: "loft-step4" },
    { src: loftEvidenceAsset.url, slug: "loft-evidence" },
    { src: loftCompletionAsset.url, slug: "loft-completion" },
    { src: loftSignColdAsset.url, slug: "loft-signs-cold" },
  ],
  trustItems: [
    "PAS 2030 Certified",
    "TrustMark Registered",
    "Insurance-Backed Guarantee",
    "Free Loft Inspection",
    "No Obligation",
  ],
  problemTitle: "Why Loft Insulation Matters",
  problemPoints: [
    {
      title: "Heat Escapes Through The Roof",
      body: "Warm air rises. Without adequate insulation at ceiling level, a large share of the heat you pay for is lost straight through the roof.",
    },
    {
      title: "Cold Upstairs Rooms",
      body: "Bedrooms that feel chillier than the rest of the house are a common sign that loft insulation is missing, too thin, or unevenly laid.",
    },
    {
      title: "Condensation Depends On Ventilation",
      body: "Insulation alone is not the full picture. Eaves airflow and balanced ventilation keep the loft healthy once heat loss is reduced.",
    },
  ],
  // The shared "clarity" section is repurposed for loft as a short
  // explainer image card if needed.
  clarityTitle: "What Good Loft Insulation Looks Like",
  clarityBody:
    "Mineral wool laid evenly between and across the joists to the current recommended depth, eaves ventilation kept clear, walkboards added where storage is needed, and electrics and vents left accessible.",
  productTitle: "What We Install",
  productPoints: [
    {
      title: "Mineral Wool To Current Standard",
      body: "Laid between and across the joists to the depth recommended by current guidance, with no gaps or compression.",
    },
    {
      title: "Eaves Airflow Maintained",
      body: "We protect the ventilation path at the eaves so the loft can breathe and condensation risk stays managed.",
    },
    {
      title: "Walkboards Where You Need Them",
      body: "Where you use the loft for storage, we add walkboards so insulation is not compressed underfoot.",
    },
    {
      title: "Electrics And Vents Kept Clear",
      body: "Downlights, cable runs and existing vents are left accessible and safely worked around.",
    },
  ],
  processSteps: [
    {
      step: 1,
      title: "Free Loft Inspection",
      description:
        "We check existing depth and condition, loft ventilation, and any signs of condensation or damp. If something needs attention first, we will tell you.",
      icon: "magnifier",
    },
    {
      step: 2,
      title: "Clear Quotation",
      description:
        "A written, property-specific quote based on what we found - covering insulation, walkboards if needed, and any sequencing notes.",
      icon: "file",
    },
    {
      step: 3,
      title: "Installation",
      description:
        "Mineral wool laid to current standard depth, eaves ventilation protected, walkboards fitted where storage is required, site left tidy.",
      icon: "drill",
    },
    {
      step: 4,
      title: "Aftercare & Guarantee",
      description:
        "Installation record, guarantee registration and aftercare guidance handed over on completion. We remain available for any follow-up questions.",
      icon: "certificate",
    },
  ],
  qualificationTitle: "Is Your Loft Suitable?",
  qualificationItems: [
    "Accessible loft space with safe access",
    "No active roof leaks or untreated damp",
    "Existing eaves ventilation is adequate (or can be restored)",
    "No existing spray foam that needs removing first",
    "Joist depth and layout allow correct insulation depth",
  ],
  includedItems: [
    "Free pre-installation loft inspection",
    "Mineral wool insulation to current standard",
    "Eaves ventilation protected during install",
    "Walkboards fitted where storage is required",
    "Installation record",
    "Insurance-backed guarantee registration",
    "Aftercare guidance",
  ],
  trustSectionTitle: "Evidence-Led Loft Inspection, Certified Install",
  trustSectionBody:
    "Every recommendation traces back to something we observed in your loft. We check depth, condition, ventilation and any signs of moisture before quoting, and install under PAS 2030 with an insurance-backed guarantee on completion.",
  benefits: [
    { title: "Warmer Upstairs Rooms", description: "Reduce heat loss through the ceiling so bedrooms feel less chilly." },
    { title: "More Even Comfort", description: "A consistent layer at ceiling level helps the whole home hold warmth for longer." },
    { title: "Healthier Loft", description: "Protected eaves airflow keeps condensation risk managed alongside insulation." },
    { title: "Documented Work", description: "Installation record and insurance-backed guarantee handed over on completion." },
  ],
  testimonialsFull: [
    {
      name: "Stephen D",
      date: "October 2025",
      serviceTag: "Loft Insulation & Heating Controls",
      source: "google",
      quote:
        "Recently had loft insulation, kitchen fan and new radiator controls fitted. All work completed within 2 days to a high standard. Workmen were fast, efficient, polite and helpful. Similarly whenever I spoke to their office staff they were both helpful and polite. I was messaged prior to each job informing me of the name and ETA of workmen. Would not hesitate to recommend.",
    },
    {
      name: "Neil Springate",
      date: "August 2025",
      serviceTag: "Boiler, Loft Insulation & Extraction",
      source: "trustpilot",
      quote:
        "I highly recommend this company. We had a new boiler, additional loft insulation and extra extraction fans fitted under a government grant scheme. It started with a two-hour survey of the house, then the ball started rolling. Great communication from everyone concerned, the installation coordinator had everything running smoothly. Everyone turned up on time, they all worked quickly and cleanly. The whole installation could not have run more smoothly.",
    },
    {
      name: "Julakha B",
      date: "December 2025",
      serviceTag: "Insulation",
      source: "google",
      quote:
        "Hi I've had insulation done today, Ryan and Nathan were amazing. They did the work, tidied up the mess and explained the process. As I have trouble keeping heat in, hopefully now this insulation will help keep heat in. Thank you so much CRS company and the team for a wonderful service.",
    },
  ],
  faqs: [
    {
      question: "What depth do you install loft insulation to?",
      answer:
        "We install to the depth recommended by current UK guidance for mineral wool at ceiling level, laid between and across the joists so coverage is even and gap-free.",
    },
    {
      question: "Will loft insulation cause condensation?",
      answer:
        "Insulation by itself does not cause condensation, but it can change how moisture behaves in the loft if airflow is restricted. We protect the eaves ventilation during install and will flag if additional ventilation is needed before work proceeds.",
    },
    {
      question: "Can I still use the loft for storage?",
      answer:
        "Yes. Where you need to store items, we fit walkboards above the insulation so it is not compressed underfoot. We confirm the area you want boarded during the inspection.",
    },
    {
      question: "How long does loft insulation take to install?",
      answer:
        "A typical loft top-up or full installation takes around half a day to a day on site with one to two installers, depending on loft size, access and whether walkboards are included.",
    },
    {
      question: "Is my loft suitable?",
      answer:
        "Most accessible lofts in sound condition are suitable. Active leaks, untreated damp, inadequate eaves ventilation, or existing spray foam may need addressing first. We confirm suitability at the free inspection and explain any sequencing.",
    },
    {
      question: "Should I top up or fully replace existing insulation?",
      answer:
        "It depends on the condition and depth of what is already there. If the existing layer is sound and well laid, a top-up is often appropriate. If it is compressed, contaminated or unevenly distributed, replacement is sometimes the better option. We base the recommendation on what we see in your loft.",
    },
  ],
  ctaTitle: "Book Your Free Loft Inspection",
  // We hand-render the page sections in lp-loft-insulation.tsx and only
  // pull these shared blocks via LpOrderedSections.
  sectionOrder: ["trust", "testimonials", "pricing", "faq"],
};
